import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { partsApi, formatPrice, getFileUrl, PART_CATEGORIES, PART_CONDITIONS } from "@/lib/api";
import QRCode from "react-qr-code";
import { 
  ArrowLeft,
  MapPin,
  Package,
  Hash,
  Phone,
  MessageCircle,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

export default function PublicPartPage() {
  const { partId } = useParams();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState(0);

  useEffect(() => {
    fetchPart();
  }, [partId]);

  const fetchPart = async () => {
    try {
      const data = await partsApi.getPublic(partId);
      setPart(data);
    } catch (error) {
      console.error("Part not found");
    } finally {
      setLoading(false);
    }
  };

  const nextPhoto = () => {
    if (part?.photos?.length > 0) {
      setCurrentPhoto((prev) => (prev + 1) % part.photos.length);
    }
  };

  const prevPhoto = () => {
    if (part?.photos?.length > 0) {
      setCurrentPhoto((prev) => (prev - 1 + part.photos.length) % part.photos.length);
    }
  };

  const whatsappNumber = "40792250058";
  const whatsappMessage = part 
    ? `Bonjour! Je suis intéressé par la pièce: ${part.name} (${part.part_id}) - ${part.compatible_brands.join(", ")}`
    : "";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-mono">Chargement...</div>
      </div>
    );
  }

  if (!part) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <Package className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="font-heading font-bold text-2xl mb-2">Pièce non trouvée</h1>
        <p className="text-muted-foreground mb-4">Cette pièce n'existe pas ou a été supprimée.</p>
        <Link to="/parts-showroom">
          <Button>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voir toutes les pièces
          </Button>
        </Link>
      </div>
    );
  }

  const categoryInfo = PART_CATEGORIES[part.category] || PART_CATEGORIES.autres;
  const conditionInfo = PART_CONDITIONS[part.condition] || PART_CONDITIONS.bon;
  const defaultImage = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/parts-showroom" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="font-heading font-black text-white">S</span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg">SCUTERSH</h1>
                <p className="text-xs text-muted-foreground">Pièces Détachées</p>
              </div>
            </Link>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button className="bg-green-600 hover:bg-green-700">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Photo Gallery */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-xl overflow-hidden bg-secondary">
              <img
                src={part.photos?.[currentPhoto] ? getFileUrl(part.photos[currentPhoto]) : defaultImage}
                alt={part.name}
                className="w-full h-full object-cover"
              />
              {part.photos?.length > 1 && (
                <>
                  <button
                    onClick={prevPhoto}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextPhoto}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {part.photos.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPhoto(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${idx === currentPhoto ? 'bg-primary' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
              {part.quantity === 0 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">ÉPUISÉ</span>
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {part.photos?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {part.photos.map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPhoto(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${idx === currentPhoto ? 'border-primary' : 'border-transparent'}`}
                  >
                    <img
                      src={getFileUrl(photo)}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-sm text-primary">{part.part_id}</span>
                <span className="text-xl">{categoryInfo.icon}</span>
                <span className={`px-2 py-1 rounded text-xs text-white ${conditionInfo.color}`}>
                  {conditionInfo.label}
                </span>
              </div>
              <h1 className="font-heading font-black text-3xl mb-2">{part.name}</h1>
              <p className="text-muted-foreground">
                Compatible: {part.compatible_brands.join(", ")}
              </p>
            </div>

            {/* Price */}
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground mb-1">Prix</p>
                <p className="font-mono font-bold text-4xl text-primary">
                  {formatPrice(part.price)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {part.quantity > 0 ? `${part.quantity} en stock` : "Épuisé"}
                </p>
              </CardContent>
            </Card>

            {/* Compatibility */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Compatibilité</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Modèles</p>
                  <p className="font-medium">{part.compatible_models.join(", ") || "Non spécifié"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Années</p>
                  <div className="flex flex-wrap gap-1">
                    {part.compatible_years.length > 0 ? (
                      part.compatible_years.map((year, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-secondary rounded text-sm font-mono">{year}</span>
                      ))
                    ) : (
                      <span className="text-muted-foreground">Non spécifié</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block">
              <Button className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg">
                <MessageCircle className="w-5 h-5 mr-2" />
                Contacter sur WhatsApp
              </Button>
            </a>

            <a href="tel:+40792250058">
              <Button variant="outline" className="w-full h-12">
                <Phone className="w-4 h-4 mr-2" />
                +40 792 250 058
              </Button>
            </a>
          </div>
        </div>

        {/* Notes */}
        {part.notes && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{part.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Source Scooter */}
        {part.source_scooter_name && (
          <Card className="mt-4">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Provient du scooter:</p>
              <p className="font-medium">{part.source_scooter_name}</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="font-heading font-bold text-lg mb-2">SCUTERSH</p>
          <p className="text-sm text-muted-foreground">
            Pièces détachées de qualité pour scooters
          </p>
          <div className="mt-4">
            <Link to="/parts-showroom">
              <Button variant="outline">
                Voir toutes les pièces
              </Button>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
